import { useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

function pctChange(a: number | null | undefined, b: number | null | undefined): number | null {
  if (a == null || b == null) return null;
  if (b === 0) return a === 0 ? 0 : null;
  return ((a - b) / Math.abs(b)) * 100;
}

function fmt(val: unknown): string {
  if (val == null) return "—";
  if (typeof val === "number") {
    return Math.abs(val) >= 1000
      ? val.toLocaleString(undefined, { maximumFractionDigits: 0 })
      : val.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(val);
}

function ChangeCell({ pct }: { pct: number | null }) {
  if (pct == null) return <span className="text-gray-400">—</span>;
  const positive = pct >= 0;
  return (
    <span className={`font-medium ${positive ? "text-green-600" : "text-red-500"}`}>
      {positive ? "+" : ""}{pct.toFixed(1)}%
    </span>
  );
}

type ComparisonRow = Record<string, unknown>;

export interface ComparisonData {
  label_a: string;
  label_b: string;
  key_column: string;
  numeric_columns?: string[];
  rows_a?: ComparisonRow[];
  rows_b?: ComparisonRow[];
}

export default function ComparisonBlock({ data }: { data: ComparisonData }) {
  const { label_a, label_b, key_column, numeric_columns, rows_a, rows_b } = data;

  const mapA = useMemo(
    () => Object.fromEntries((rows_a || []).map((r) => [String(r[key_column]), r])),
    [rows_a, key_column],
  );
  const mapB = useMemo(
    () => Object.fromEntries((rows_b || []).map((r) => [String(r[key_column]), r])),
    [rows_b, key_column],
  );

  const keys = useMemo(() => {
    const seen = new Set<string>();
    [...(rows_a || []), ...(rows_b || [])].forEach((r) => seen.add(String(r[key_column])));
    return [...seen];
  }, [rows_a, rows_b, key_column]);

  const numCols = numeric_columns && numeric_columns.length > 0 ? numeric_columns : [];

  const chartCol = numCols[0];
  const chartData = useMemo(() => {
    if (!chartCol) return [];
    return keys.slice(0, 15).map((k) => ({
      name: String(k),
      [label_a]: mapA[k]?.[chartCol] ?? 0,
      [label_b]: mapB[k]?.[chartCol] ?? 0,
    }));
  }, [keys, chartCol, mapA, mapB, label_a, label_b]);

  return (
    <div className="mt-3 space-y-4">
      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 text-xs">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="whitespace-nowrap px-3 py-2 text-left font-semibold text-gray-600">
                {key_column}
              </th>
              {numCols.map((col) => (
                <>
                  <th key={`a-${col}`} className="whitespace-nowrap px-3 py-2 text-right font-semibold text-blue-600">
                    {label_a}
                  </th>
                  <th key={`b-${col}`} className="whitespace-nowrap px-3 py-2 text-right font-semibold text-indigo-500">
                    {label_b}
                  </th>
                  <th key={`d-${col}`} className="whitespace-nowrap px-3 py-2 text-right font-semibold text-gray-500">
                    Change
                  </th>
                </>
              ))}
              {numCols.length === 0 && (
                <>
                  <th className="whitespace-nowrap px-3 py-2 text-right font-semibold text-blue-600">{label_a}</th>
                  <th className="whitespace-nowrap px-3 py-2 text-right font-semibold text-indigo-500">{label_b}</th>
                </>
              )}
            </tr>
            {numCols.length > 1 && (
              <tr className="border-t border-gray-200 bg-gray-50">
                <td className="px-3 py-1 text-xs font-medium text-gray-400 italic">{numCols.join(" · ")}</td>
                {numCols.map((col) => (
                  <td key={col} colSpan={3} className="px-3 py-1 text-center text-xs text-gray-400">{col}</td>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {keys.map((k, i) => {
              const ra = mapA[k];
              const rb = mapB[k];
              return (
                <tr key={k} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  <td className="whitespace-nowrap px-3 py-2 font-medium text-gray-700">{fmt(k)}</td>
                  {numCols.length > 0 ? numCols.map((col) => (
                    <>
                      <td key={`a-${col}`} className="whitespace-nowrap px-3 py-2 text-right text-gray-700">{fmt(ra?.[col])}</td>
                      <td key={`b-${col}`} className="whitespace-nowrap px-3 py-2 text-right text-gray-700">{fmt(rb?.[col])}</td>
                      <td key={`d-${col}`} className="whitespace-nowrap px-3 py-2 text-right">
                        <ChangeCell pct={pctChange(ra?.[col] as number, rb?.[col] as number)} />
                      </td>
                    </>
                  )) : (
                    <>
                      <td className="whitespace-nowrap px-3 py-2 text-right text-gray-700">{ra ? "✓" : "—"}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-right text-gray-700">{rb ? "✓" : "—"}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Chart — only if there's a numeric column and more than 1 row */}
      {chartCol && chartData.length > 1 && (
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="mb-2 text-xs font-medium text-gray-500">{chartCol}</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" interval={0} />
              <YAxis tick={{ fontSize: 11 }} width={60} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey={label_a} fill="#3b82f6" radius={[3, 3, 0, 0]} />
              <Bar dataKey={label_b} fill="#818cf8" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
